// SPDX-License-Identifier: MIT
/**
 * Interface controls. Every state is a 0–1 number driven by the scene (usually `beat.seg`), so any
 * two states blend continuously and every frame can be scrubbed. Components never read the clock.
 */
import { TAU, clamp, lerp } from '../../core/math.ts';
import { mix } from '../color.ts';
import { type Box, type IconName, elevation, fitLabel, icon, inset, panel, rrect, scaleBox, skeleton, } from './draw.ts';
import type { UiTokens } from './tokens.ts';
type Ctx = CanvasRenderingContext2D;
const withAlpha = (ctx: Ctx, alpha: number, draw: () => void) => {
    if (alpha <= 0.002)
        return;
    const prev = ctx.globalAlpha;
    ctx.globalAlpha = prev * alpha;
    draw();
    ctx.globalAlpha = prev;
};
/** Text with explicit alignment, vertically centred on `y`. */
export function label(ctx: Ctx, text: string, x: number, y: number, color: string, align: CanvasTextAlign = 'left'): void {
    ctx.fillStyle = color;
    ctx.textAlign = align;
    ctx.textBaseline = 'middle';
    ctx.fillText(text, x, y);
}
// --- Button -------------------------------------------------------------------------------------------
export type ButtonState = Partial<{
    variant: 'primary' | 'secondary' | 'ghost';
    /** Pressed down (shrinks a little, darkens). */
    press: number;
    /** The label gives way to a spinner. */
    loading: number;
    /** The spinner gives way to a check mark. */
    done: number;
    /** Spinner angle in turns (pass e.g. `env.localBeat`). */
    spin: number;
    icon: IconName;
}>;
const buttonPx = (tk: UiTokens, h: number) => clamp(Math.round(h * 0.42), tk.type.caption, tk.type.title);
/** Width a button needs for `text` at height `h`. */
export function buttonWidth(ctx: Ctx, tk: UiTokens, text: string, h: number = tk.control.md, withIcon = false): number {
    const px = buttonPx(tk, h);
    ctx.font = tk.font(px, 600);
    return Math.ceil(ctx.measureText(text).width + h * 0.9 + (withIcon ? px * 1.5 : 0));
}
export function button(ctx: Ctx, tk: UiTokens, b: Box, text: string, s: ButtonState = {}): void {
    const { variant = 'primary', press = 0, loading = 0, done = 0, spin = 0 } = s;
    const box = scaleBox(b, 1 - 0.035 * press);
    const r = Math.min(tk.radius.md, box.h / 2);
    const primary = variant === 'primary';
    const fg = primary ? tk.onAccent : tk.text;
    if (primary) {
        elevation(ctx, tk, box, r, 0.7 * (1 - press));
        rrect(ctx, box, r);
        ctx.fillStyle = mix(tk.accent, '#000000', 0.14 * press);
        ctx.fill();
        // A soft highlight along the top edge gives the button body.
        ctx.save();
        rrect(ctx, box, r);
        ctx.clip();
        const g = ctx.createLinearGradient(0, box.y, 0, box.y + box.h);
        g.addColorStop(0, `rgba(255,255,255,${0.16 * (1 - press)})`);
        g.addColorStop(0.5, 'rgba(255,255,255,0)');
        ctx.fillStyle = g;
        ctx.fillRect(box.x, box.y, box.w, box.h);
        ctx.restore();
    }
    else if (variant === 'secondary') {
        rrect(ctx, box, r);
        ctx.fillStyle = mix(tk.raised, tk.text, 0.05 * press);
        ctx.fill();
        ctx.lineWidth = 1.5;
        ctx.strokeStyle = tk.border;
        ctx.stroke();
    }
    else if (press > 0) {
        withAlpha(ctx, press, () => {
            rrect(ctx, box, r);
            ctx.fillStyle = tk.accentSoft;
            ctx.fill();
        });
    }
    const cx = box.x + box.w / 2;
    const cy = box.y + box.h / 2;
    const px = buttonPx(tk, box.h);
    withAlpha(ctx, 1 - Math.max(loading, done), () => {
        const iconW = s.icon ? px * 1.5 : 0;
        fitLabel(ctx, tk, text, box.w - box.h * 0.9 - iconW, px, 600);
        const w = ctx.measureText(text).width + iconW;
        if (s.icon)
            icon(ctx, s.icon, cx - w / 2, cy - px * 0.55, px * 1.1, fg);
        label(ctx, text, cx - w / 2 + iconW, cy, primary ? fg : variant === 'ghost' ? tk.accentText : fg);
    });
    withAlpha(ctx, loading * (1 - done), () => {
        ctx.beginPath();
        const a = spin * TAU;
        ctx.arc(cx, cy, px * 0.48, a, a + TAU * 0.72);
        ctx.lineWidth = 3;
        ctx.lineCap = 'round';
        ctx.strokeStyle = fg;
        ctx.stroke();
    });
    if (done > 0)
        icon(ctx, 'check', cx - px * 0.65, cy - px * 0.65, px * 1.3, fg, done);
}
// --- Text field ---------------------------------------------------------------------------------------
export type FieldState = Partial<{
    /** What has been typed so far (see `typing`). */
    text: string;
    placeholder: string;
    focus: number;
    /** Caret opacity. */
    caret: number;
    icon: IconName;
}>;
export function field(ctx: Ctx, tk: UiTokens, b: Box, s: FieldState = {}): void {
    const { text = '', placeholder = '', focus = 0, caret = 0 } = s;
    const r = tk.radius.md;
    withAlpha(ctx, focus, () => {
        rrect(ctx, inset(b, -5), r + 5);
        ctx.lineWidth = 6;
        ctx.strokeStyle = mix(tk.accent, tk.bg, 0.55);
        ctx.stroke();
    });
    rrect(ctx, b, r);
    ctx.fillStyle = tk.sunken;
    ctx.fill();
    ctx.lineWidth = 1.5;
    ctx.strokeStyle = tk.border;
    ctx.stroke();
    withAlpha(ctx, focus, () => {
        rrect(ctx, b, r);
        ctx.strokeStyle = tk.accent;
        ctx.lineWidth = 2;
        ctx.stroke();
    });
    const px = clamp(Math.round(b.h * 0.42), tk.type.caption, tk.type.title);
    const pad = b.h * 0.34;
    let x0 = b.x + pad;
    if (s.icon) {
        icon(ctx, s.icon, x0, b.y + b.h / 2 - px * 0.55, px * 1.1, tk.muted);
        x0 += px * 1.1 + pad * 0.6;
    }
    const maxW = b.x + b.w - pad - x0;
    const cy = b.y + b.h / 2;
    ctx.font = tk.font(px, 500);
    const w = text ? ctx.measureText(text).width : 0;
    ctx.save();
    ctx.beginPath();
    ctx.rect(x0 - 2, b.y, maxW + 8, b.h);
    ctx.clip();
    if (text)
        label(ctx, text, x0 - Math.max(0, w - maxW), cy, tk.text);
    else if (placeholder)
        label(ctx, placeholder, x0, cy, tk.faint);
    ctx.restore();
    withAlpha(ctx, caret, () => {
        ctx.fillStyle = tk.accent;
        ctx.fillRect(x0 + Math.min(w, maxW) + 3, cy - px * 0.62, 3, px * 1.24);
    });
}
// --- Toggle -------------------------------------------------------------------------------------------
/** A switch with its left edge at `x`, vertically centred on `y`; returns its width. */
export function toggle(ctx: Ctx, tk: UiTokens, x: number, y: number, h: number, on: number): number {
    const w = h * 1.8;
    const track: Box = { x, y: y - h / 2, w, h };
    rrect(ctx, track, h / 2);
    ctx.fillStyle = mix(tk.track, tk.accent, clamp(on));
    ctx.fill();
    const kr = h / 2 - 4;
    const kx = lerp(x + h / 2, x + w - h / 2, on);
    const knob: Box = { x: kx - kr, y: y - kr, w: kr * 2, h: kr * 2 };
    elevation(ctx, tk, knob, kr, 0.5);
    ctx.beginPath();
    ctx.arc(kx, y, kr, 0, TAU);
    ctx.fillStyle = '#FFFFFF';
    ctx.fill();
    return w;
}
// --- Badge, avatar ------------------------------------------------------------------------------------
export type BadgeState = Partial<{
    tone: 'accent' | 'neutral';
    pop: number;
    align: 'left' | 'center';
    icon: IconName;
}>;
/** A pill label; (x, y) is its left edge (or centre) and vertical centre. Returns its size. */
export function badge(ctx: Ctx, tk: UiTokens, x: number, y: number, text: string, s: BadgeState = {}): {
    w: number;
    h: number;
} {
    const { tone = 'accent', pop = 1, align = 'left' } = s;
    const px = tk.type.caption;
    const h = 44;
    ctx.font = tk.font(px, 600);
    const iconW = s.icon ? px + 8 : 0;
    const w = ctx.measureText(text).width + 36 + iconW;
    const left = align === 'center' ? x - w / 2 : x;
    if (pop <= 0)
        return { w, h };
    const k = clamp(pop);
    withAlpha(ctx, k, () => {
        ctx.save();
        ctx.translate(left + w / 2, y);
        ctx.scale(0.7 + 0.3 * k, 0.7 + 0.3 * k);
        rrect(ctx, { x: -w / 2, y: -h / 2, w, h }, h / 2);
        ctx.fillStyle = tone === 'accent' ? tk.accentSoft : tk.skeleton;
        ctx.fill();
        const color = tone === 'accent' ? tk.accentText : tk.muted;
        if (s.icon)
            icon(ctx, s.icon, -w / 2 + 16, -px / 2, px, color);
        ctx.font = tk.font(px, 600);
        label(ctx, text, -w / 2 + 18 + iconW, 1, color);
        ctx.restore();
    });
    return { w, h };
}
/** A plain disc in one of four palette tints (no invented faces or initials). */
export function avatar(ctx: Ctx, tk: UiTokens, cx: number, cy: number, r: number, tint = 0): void {
    const tints = [
        tk.accent,
        mix(tk.accent, tk.surface, 0.5),
        mix(tk.text, tk.surface, 0.55),
        mix(tk.accent, tk.text, 0.35),
    ];
    ctx.beginPath();
    ctx.arc(cx, cy, r, 0, TAU);
    ctx.fillStyle = tints[((tint % 4) + 4) % 4]!;
    ctx.fill();
}
// --- List row -----------------------------------------------------------------------------------------
export type RowState = Partial<{
    /** Real text from the brand; without it the row shows skeleton bars. */
    title: string;
    meta: string;
    /** Avatar tint, or false for none. */
    avatar: number | false;
    icon: IconName;
    /** Skeleton bar widths as fractions of the free width. */
    bars: [
        number,
        number
    ];
    selected: number;
    /** Trailing check mark drawn on. */
    check: number;
    /** Content fade-in. */
    reveal: number;
}>;
export function row(ctx: Ctx, tk: UiTokens, b: Box, s: RowState = {}): void {
    const { selected = 0, check = 0, reveal = 1, bars = [0.55, 0.32] } = s;
    withAlpha(ctx, selected, () => {
        rrect(ctx, b, tk.radius.md);
        ctx.fillStyle = tk.accentSoft;
        ctx.fill();
    });
    const pad = Math.max(16, b.h * 0.22);
    const cy = b.y + b.h / 2;
    let x = b.x + pad;
    withAlpha(ctx, reveal, () => {
        if (s.icon) {
            const d = b.h * 0.56;
            rrect(ctx, { x, y: cy - d / 2, w: d, h: d }, tk.radius.sm);
            ctx.fillStyle = tk.accentSoft;
            ctx.fill();
            icon(ctx, s.icon, x + d * 0.2, cy - d * 0.3, d * 0.6, tk.accentText);
            x += d + pad;
        }
        else if (s.avatar !== false && s.avatar !== undefined) {
            const r = b.h * 0.28;
            avatar(ctx, tk, x + r, cy, r, s.avatar);
            x += r * 2 + pad;
        }
        const right = b.x + b.w - pad - (check > 0 ? 40 : 0);
        if (s.title) {
            const twoLines = !!s.meta;
            fitLabel(ctx, tk, s.title, right - x, tk.type.body, 600);
            label(ctx, s.title, x, twoLines ? cy - 15 : cy, tk.text);
            if (s.meta) {
                fitLabel(ctx, tk, s.meta, right - x, tk.type.caption, 500);
                label(ctx, s.meta, x, cy + 19, tk.muted);
            }
        }
        else {
            skeleton(ctx, tk, x, cy - 16, (right - x) * bars[0], 14);
            skeleton(ctx, tk, x, cy + 6, (right - x) * bars[1], 12, 0.7);
        }
    });
    if (check > 0)
        icon(ctx, 'check', b.x + b.w - pad - 32, cy - 16, 32, tk.accentText, check);
}
// --- Toast --------------------------------------------------------------------------------------------
export type ToastState = Partial<{
    body: string;
    icon: IconName;
    enter: number;
    progress: number;
}>;
/** Height a toast needs. */
export const toastHeight = (s: {
    body?: string;
}) => (s.body ? 112 : 88);
/** A notification card. `enter` slides it up into `b`; `progress` runs its auto-dismiss bar. */
export function toast(ctx: Ctx, tk: UiTokens, b: Box, title: string, s: ToastState = {}): void {
    const { enter = 1, icon: name = 'check' } = s;
    const k = clamp(enter);
    if (k <= 0)
        return;
    withAlpha(ctx, Math.min(1, k * 1.4), () => {
        ctx.save();
        ctx.translate(b.x + b.w / 2, b.y + b.h / 2 + (1 - k) * 32);
        const sc = 0.96 + 0.04 * k;
        ctx.scale(sc, sc);
        const box: Box = { x: -b.w / 2, y: -b.h / 2, w: b.w, h: b.h };
        panel(ctx, tk, box, { fill: tk.raised, lift: 2 });
        const d = 48;
        const ix = box.x + 24;
        ctx.beginPath();
        ctx.arc(ix + d / 2, 0, d / 2, 0, TAU);
        ctx.fillStyle = tk.accent;
        ctx.fill();
        icon(ctx, name, ix + 10, -14, 28, tk.onAccent, name === 'check' ? clamp((k - 0.4) / 0.6) : 1);
        const tx = ix + d + 20;
        const maxW = box.x + box.w - 24 - tx;
        fitLabel(ctx, tk, title, maxW, tk.type.body, 600);
        label(ctx, title, tx, s.body ? -17 : 0, tk.text);
        if (s.body) {
            fitLabel(ctx, tk, s.body, maxW, tk.type.caption, 500);
            label(ctx, s.body, tx, 19, tk.muted);
        }
        if (s.progress !== undefined) {
            ctx.save();
            rrect(ctx, box, tk.radius.lg);
            ctx.clip();
            ctx.fillStyle = tk.accent;
            ctx.fillRect(box.x, box.y + box.h - 4, box.w * (1 - clamp(s.progress)), 4);
            ctx.restore();
        }
        ctx.restore();
    });
}
